import BaseComponent from "./base_component_controller";
import {
  ensureResultsMonotonic,
  nestedGroupBy,
  extractCascade,
} from "hb/lib/collection_helpers";

import { useResize } from "hb/insights/use_resize";
import { useChartConfig } from "hb/insights/use_chart_config";
import { dimensionTypes, axisType } from "hb/insights/chart";
import { isDatetimeType, isNumberType } from "hb/insights/types";

export default class extends BaseComponent {
  static viewId = "area";

  static get values() {
    return {
      ...BaseComponent.values,

      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object,
    };
  }

  static configs = [
    "xField",
    "yField",
    "zField",
    { name: "groupType", default: "events" },
    { name: "groups", default: "{}", type: "json" },
    { name: "stacked", default: true, type: "boolean" },
  ];

  connect() {
    super.connect();

    useChartConfig(this);
  }

  outletsConnected() {
    super.outletsConnected();

    useResize(this, { element: this.outletComponent("chart").chartTarget });
    this.initQuery();
  }

  queryContextDefaults() {
    const { meta } = this.query;
    let config = {};
    const fieldTypes = Object.entries(meta.fieldTypes);

    const xRes = extractCascade(
      fieldTypes,
      ([, t]) => isDatetimeType(t),
      ([, t]) => isNumberType(t),
    );

    const yRes = extractCascade(
      xRes[1],
      ([, t]) => isNumberType(t),
      ([, t]) => isDatetimeType(t),
    );

    const zRes = extractCascade(yRes[1], ([, t]) => t === "string");

    if (xRes[0] !== undefined) config.xField = xRes[0][0];
    if (yRes[0] !== undefined) config.yField = yRes[0][0];
    if (zRes[0] !== undefined) config.zField = zRes[0][0];

    return config;
  }

  validate() {
    let { xField, yField, zField, groupType } = this.config;

    if (!xField || (!yField && groupType === "events")) {
      this.errorMessage =
        "Select fields in chart config or update data to fit chart";
    } else if (this.hasUnknownFields(xField, yField, zField)) {
      this.errorMessage = "Unknown field selected";
    }
  }

  processConfig() {
    if (this.shouldSkip) return;

    let { groupType, groups, xField, yField, zField } = this.config;
    let { results, meta } = this.query;

    let groupConfig;
    let colorIdx = -1;
    const gatherColor = (n) => {
      colorIdx = (colorIdx + 1) % this.colorValues.length;
      return groups[n]?.color || this.colorValues[colorIdx];
    };

    if (groupType === "fields") {
      const colNames = meta.fields.filter((n) => n != xField);
      groupConfig = colNames.map((name, idx) => ({
        idx,
        name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name),
        axis: groups[name]?.axis || "left",
      }));
    } else if (zField) {
      const zDomain = Array.from(new Set(results.map((d) => d.field(zField))));
      this.derivedConfig.zDomain = zDomain;
      groupConfig = zDomain.map((name, idx) => ({
        idx,
        name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name),
        axis: groups[name]?.axis || "left",
      }));
    } else {
      groupConfig = [
        {
          idx: 0,
          name: yField,
          color: gatherColor(yField),
          axis: "left",
        },
      ];
    }

    this.derivedConfig.groupConfig = groupConfig;
  }

  renderConfig() {
    const { meta } = this.query;

    ["xField", "yField", "zField"].forEach((name) => {
      let allFields;
      if (name === "zField") {
        allFields = ["__empty", ...meta.fields];
      } else {
        allFields = meta.fields;
      }

      this.find(`[data-input-name="${name}"] ul`).innerHTML = allFields
        .map((field) =>
          this.comboboxItemTemplate({
            classes: this[name] === field ? "selected" : "",
            value: field,
            id: field,
            display: field,
            ...(field == "__empty" && {
              display: null,
              badge: "none",
            }),
          }),
        )
        .join("\n");
    });

    this.find(
      `input[name="groupType"][id="${this.config.groupType}"]`,
    ).checked = "true";
    this.find(`input[name="stacked"]`).checked = this.config.stacked;
    this.find(`input[name="xField"]`).value = this.config.xField || "";
    this.find(`input[name="yField"]`).value = this.config.yField || "";
    this.find(`input[name="zField"]`).value = this.config.zField || "";

    this.renderFieldGroups(this.derivedConfig.groupConfig);
  }

  render() {
    let { meta, results } = this.query;
    let { xField, yField, zField, groupType, groups, stacked } = this.config;

    if (!xField || !results) {
      return;
    }

    const xFieldType = meta.type(xField);
    const lineSeriesDefault = {
      type: "line",
      areaStyle: {},
      stack: stacked ? "default" : null,
      animation: false,
      showSymbol: false,
    };

    results = ensureResultsMonotonic(results, xField);

    let zDomain, series, dataset, xAxis, yAxis;

    // Build out dataset with broken out zFields per x value
    if (groupType === "fields") {
      const colNames = meta.fields.filter((n) => n != xField);

      dataset = {
        dimensions: [
          { name: xField, type: dimensionTypes[xFieldType] },
          ...colNames,
        ],
        source: results.map((r) => r.slice(xField, ...colNames)),
      };

      series = this.derivedConfig.groupConfig
        .filter(({ disabled }) => !disabled)
        .map(({ name, color }) => ({
          ...lineSeriesDefault,
          name,
          itemStyle: { color },
          dimensions: [xField, name],
        }));

      yAxis = { name: yField };
    } else if (zField) {
      zDomain = Array.from(new Set(results.map((d) => d.field(zField))));

      const nested = nestedGroupBy(
        results,
        (r) => [r.field(xField), r.field(zField)],
        (r) => r.field(yField),
      );

      const folded = Object.entries(nested).map(([x, res]) => {
        res[xField] = x;
        return res;
      });

      dataset = {
        source: folded,
        dimensions: [
          { name: xField, type: dimensionTypes[xFieldType] },
          ...zDomain,
        ],
      };

      if (zDomain.some((z) => groups[z]?.axis === "right")) {
        yAxis = [
          {
            ...this.defaultYAxis,
            alignTicks: true,
            name: "",
          },
          {
            name: "",
            alignTicks: true,
            nameTextStyle: { align: "right" },
          },
        ];
      } else {
        yAxis = { ...this.defaultYAxis, name: "" };
      }

      series = this.derivedConfig.groupConfig
        .filter(({ disabled }) => !disabled)
        .map(({ name, color, axis }) => ({
          ...lineSeriesDefault,
          yAxisIndex: axis === "right" ? 1 : undefined,
          name,
          itemStyle: { color },
          dimensions: [xField, name],
        }));
    } else {
      dataset = {
        dimensions: [xField, yField],
        source: results.map((r) => r.slice(yField, xField)),
      };
      series = [
        {
          ...lineSeriesDefault,
          itemStyle: { color: this.derivedConfig.groupConfig[0].color },
          name: yField,
        },
      ];
      yAxis = { ...this.defaultYAxis, name: "" };
    }

    xAxis = {
      type: axisType(xFieldType),
      boundaryGap: false,
      name: xField,
      // Keeps named axis, but pushes it outside the viewing area
      nameGap: 30,
      min: "dataMin",
      max: "dataMax",
    };

    if (isDatetimeType(xFieldType)) {
      if (meta.queryStartAt) {
        xAxis.min = meta.queryStartAt;
      }

      if (meta.queryEndAt) {
        xAxis.max = meta.queryEndAt;
      }
    }

    const options = {
      ...this.defaultChartConfig,
      dataset,
      series,
      yAxis,
      legend: null,
      xAxis,
      tooltip: {
        ...this.defaultChartConfig.tooltip,
        trigger: "axis",
      },
    };

    this.outletComponent("chart").setChartOptions(options);
    this.outletComponent("legend").render(this.derivedConfig.groupConfig);
  }
}
